import React, { useEffect, useRef, useState, useCallback } from 'react';
import { improveItem, onboardingWebSocketService } from '../services/onboardingWebSocketService';
import { ProgressBar } from './ui/ProgressBar';
import { useVoiceTranscript } from '../hooks/useVoiceTranscript';
import {
  ONBOARDING_STAGES,
  ONBOARDING_STAGE_LABELS,
  isOnboardingStage,
  type ImproveItemCurrent,
  type OnboardingStage,
  type SmartWizardRequest,
} from '../types/onboarding';

/**
 * The knowledge-base setup wizard — talks to onboarding_orchestrator_service.py
 * over onboardingWebSocketService. Self-contained: manages its own WS
 * connection, needs nothing from its mount point beyond an exit callback.
 * Rendered persistently beside SeededOnboardingReview. Section and item CTAs
 * send objectives into this same conversation without mounting another UI.
 *
 * Layout: a running conversation thread (question+answer pairs stay visible
 * as they happen, not filed away), one live question+input card (voice or
 * text, always editable before submit — nothing auto-sends), plus a compact
 * growing snapshot of what's been captured so far rather than an itemized
 * table. Captured rows are draft-only: the durable edit/reject/publish step
 * happens in SeededOnboardingReview against the same KnowledgeGeneratorTask
 * draft collection the seeded flow uses.
 */
export interface OnboardingWizardProps {
  /** Called when the customer closes or finishes the wizard, so the mount
   * point can clear any setup-mode URL state and refresh KB data. */
  onExit: () => void;
  presentation?: 'page' | 'guide';
  request?: SmartWizardRequest | null;
  draftTaskId?: string;
  /** Lets the review surface update its compact "what changed" log after
   * capture, without changing this wizard's own captured-items table. */
  onItemCaptured?: (item: CapturedItem) => void;
  onItemPending?: (item: CapturedItem) => void;
  showCapturedList?: boolean;
  autoBegin?: boolean;
  /** Keep an already-populated additive draft quiet until an explicit Add or
   * Improve CTA supplies a request. */
  idleUntilRequested?: boolean;
}

type ConnectionState = 'connecting' | 'connected' | 'disconnected';

interface ObjectionFaqDraft {
  documentId?: string;
  collection: 'objections' | 'faqs';
  question: string;
  answer: string;
  category: string | null;
  variations: { documentId: string; text: string }[];
  pending?: boolean;
  source?: 'manual' | 'voice';
}

interface LandmineDraft {
  documentId?: string;
  trigger: string;
  triggerVariations?: string[];
  canSay: { point: string; context?: string }[];
  cannotSay: { point: string; reason?: string }[];
  landmineType?: 'legal_risk' | 'detractor';
  priority?: 'high' | 'medium' | 'low';
  quickTips?: string;
  /** Freshly-drafted items are always 'pending'; ones fetched back in on a
   *  resumed session may already be 'approved' if a human reviewed them on
   *  the Knowledge Base page in the meantime. */
  approvalStatus: string;
  pending?: boolean;
}

interface CompetitorDraft {
  documentId?: string;
  name: string;
  advantages: string[];
  weaknesses: string[];
  pending?: boolean;
}

interface CompanyContextDraft {
  field: string;
  value: string | string[];
}

const improveValueToText = (current: ImproveItemCurrent): { question?: string; answer: string } => {
  if ('question' in current) return current;
  return { answer: Array.isArray(current.value) ? current.value.join('\n') : current.value };
};

export interface CapturedItem {
  id: string;
  stage: OnboardingStage;
  kind: OnboardingStage;
  draft: ObjectionFaqDraft | LandmineDraft | CompetitorDraft | CompanyContextDraft;
}

/** One question+answer exchange, kept visible in the running conversation
 *  thread instead of disappearing the moment it's captured.
 *
 *  `status` tracks the turn's own submit_turn lifecycle rather than
 *  assuming every answer the instant it's typed reads as "successful":
 *  - 'pending': submitTurn() has been sent, no terminal response yet.
 *  - 'accepted': the server responded with something other than a failure
 *    for this turn's clientEventId (item_drafted/item_pending/question/
 *    stage transition all count).
 *  - 'failed': a draft_error, state_conflict, or error carried this exact
 *    clientEventId back.
 */
interface ConversationTurn {
  id: string;
  question: string;
  answer: string;
  clientEventId: string;
  status: 'pending' | 'accepted' | 'failed';
  inputSource: 'typed' | 'voice';
  /** Present only when status is 'failed' — shown inline on the bubble so
   *  the reason isn't only in the (easy to miss) global banner. */
  failureMessage?: string;
}

const KNOWN_KINDS = new Set<CapturedItem['kind']>([
  'objections', 'faqs', 'landmines', 'competitors', 'company_context',
]);
const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'string');

/** The backend sends `kind`/`draft` as loosely-typed JSON over the wire —
 *  this repo's onboarding orchestrator has been under active rearchitecture
 *  (the tool-calling agent loop), so a version-skewed deploy or a bug in a
 *  new capture path producing an unexpected shape is a real, not
 *  theoretical, risk right now. Validate before ever casting into
 *  CapturedItem: every downstream renderer (renderRow, summaryHighlightText,
 *  buildCaptureConfirmation) assumes `question`/`trigger`/`name` and various
 *  arrays exist, and a bad cast would only surface as a crash deep inside
 *  one of those, far from the actual malformed payload. */
const isValidCapturedDraft = (kind: unknown, draft: unknown): draft is CapturedItem['draft'] => {
  if (!KNOWN_KINDS.has(kind as CapturedItem['kind']) || typeof draft !== 'object' || draft === null) {
    return false;
  }
  const d = draft as Record<string, unknown>;
  switch (kind as CapturedItem['kind']) {
    case 'objections':
    case 'faqs':
      return typeof d.question === 'string' && typeof d.answer === 'string' && Array.isArray(d.variations ?? []);
    case 'landmines':
      return typeof d.trigger === 'string' && Array.isArray(d.canSay ?? []) && Array.isArray(d.cannotSay ?? []);
    case 'competitors':
      return typeof d.name === 'string' && isStringArray(d.advantages ?? []) && isStringArray(d.weaknesses ?? []);
    case 'company_context':
      return typeof d.field === 'string' && (typeof d.value === 'string' || isStringArray(d.value));
    default:
      return false;
  }
};

/** Warmer, jargon-free names for the chapter stepper and header — the
 *  internal taxonomy in STAGE_LABELS stays put for the detail/grouping view
 *  (where a manager benefits from precise category names), but during live
 *  capture the section chrome shouldn't read like a form's field-group
 *  labels. */
const STAGE_DISPLAY_NAMES: Record<OnboardingStage, string> = {
  company_context: 'The Basics',
  objections: 'Tough Conversations',
  faqs: 'Common Questions',
  landmines: 'What Not to Say',
  competitors: 'The Competition',
};

const COMPANY_CONTEXT_FIELD_LABELS: Record<string, string> = {
  whatYouSell: 'What you sell',
  targetAudience: 'Who buys it',
  howItWorks: 'How it works',
  certifications: 'Certifications',
  differentiators: 'Differentiators',
};

const isObjectionOrFaq = (item: CapturedItem): item is CapturedItem & { draft: ObjectionFaqDraft } =>
  item.kind === 'objections' || item.kind === 'faqs';
const isLandmine = (item: CapturedItem): item is CapturedItem & { draft: LandmineDraft } =>
  item.kind === 'landmines';
const isCompetitor = (item: CapturedItem): item is CapturedItem & { draft: CompetitorDraft } =>
  item.kind === 'competitors';
const isCompanyContext = (item: CapturedItem): item is CapturedItem & { draft: CompanyContextDraft } =>
  item.kind === 'company_context';

/** A short, specific "you just made this smarter" line built from what was
 *  actually said — used both as the capture toast and (via
 *  summaryHighlightText for the plain value) the running snapshot. Deliberately
 *  not a generic "X added to your draft" receipt: the same event should read
 *  as progress, not as a save confirmation. */
const buildCaptureConfirmation = (item: CapturedItem): string => {
  if (isCompanyContext(item)) {
    const label = COMPANY_CONTEXT_FIELD_LABELS[item.draft.field] || item.draft.field;
    return `Got it. "${label}" is set.`;
  }
  if (item.kind === 'objections') {
    return `Your AI can now handle: "${(item.draft as ObjectionFaqDraft).question}"`;
  }
  if (item.kind === 'faqs') {
    return `Your AI can now answer: "${(item.draft as ObjectionFaqDraft).question}"`;
  }
  if (isLandmine(item)) {
    return `Added a guardrail around "${item.draft.trigger}". Reps will know what to avoid.`;
  }
  if (isCompetitor(item)) {
    return `Your AI now knows how you stack up against ${item.draft.name}.`;
  }
  return 'Added to your draft.';
};

/** Plain label for one captured item, used in the running snapshot's
 *  "Just added" line — separate from buildCaptureConfirmation so the toast
 *  and the snapshot never render identical text at the same time. */
const summaryHighlightText = (item: CapturedItem): string => {
  if (isCompanyContext(item)) {
    return COMPANY_CONTEXT_FIELD_LABELS[item.draft.field] || item.draft.field;
  }
  if (isObjectionOrFaq(item)) {
    return item.draft.question;
  }
  if (isLandmine(item)) {
    return item.draft.trigger;
  }
  if (isCompetitor(item)) {
    return item.draft.name;
  }
  return '';
};

/** Singular/plural noun for each capture kind, used to build the snapshot's
 *  summary chips (e.g. "3 objections", "1 landmine flagged"). */
const STAGE_CHIP_NOUNS: Record<CapturedItem['kind'], [string, string]> = {
  company_context: ['company detail', 'company details'],
  objections: ['objection', 'objections'],
  faqs: ['FAQ', 'FAQs'],
  landmines: ['landmine flagged', 'landmines flagged'],
  competitors: ['competitor mapped', 'competitors mapped'],
};

const isPending = (item: CapturedItem) => (item.draft as { pending?: boolean }).pending === true;

let idCounter = 0;
const nextId = () => `item-${Date.now()}-${idCounter++}`;

// ---------------------------------------------------------------------------
// Small inline building blocks
// ---------------------------------------------------------------------------

const RowShell: React.FC<{
  badge?: React.ReactNode;
  children: React.ReactNode;
}> = ({ badge, children }) => (
  <div className="bg-surface border border-hairline rounded-lg p-3.5">
    <div className="flex-1 min-w-0">{badge}</div>
    {children}
  </div>
);

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  onExit,
  presentation = 'page',
  request,
  draftTaskId,
  onItemCaptured,
  onItemPending,
  showCapturedList = true,
  autoBegin = false,
  idleUntilRequested = false,
}) => {
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const [stages, setStages] = useState<OnboardingStage[]>([]);
  const [currentStage, setCurrentStage] = useState<OnboardingStage | null>(null);
  const [progressPercent, setProgressPercent] = useState(0);

  const [sessionWelcome, setSessionWelcome] = useState<string | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [isResumed, setIsResumed] = useState(false);
  const [, setStageIntroText] = useState<string | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<string | null>(null);
  const [readySent, setReadySent] = useState(false);

  const [inputValue, setInputValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [captureConfirmation, setCaptureConfirmation] = useState<string | null>(null);

  // The running conversation thread — question+answer pairs stay visible
  // here once submitted, instead of disappearing the moment they're filed
  // (see CapturedItem below, which is the structured/reviewable side of the
  // same event). Each entry starts as an optimistic local echo (status:
  // 'pending') of what the user just did — the answer bubble itself never
  // waits on the network to appear — but its `status` DOES track server
  // confirmation now: markTurnStatus below flips it to 'accepted' or
  // 'failed' by matching the turn's own clientEventId back against
  // whichever submit_turn response actually arrives, so a dropped
  // connection or a rejected turn reads as visibly unresolved rather than
  // identical to a successful one.
  const [history, setHistory] = useState<ConversationTurn[]>([]);
  /** Flips one specific history entry's status by clientEventId — the
   *  common correlation point every submit_turn response (item_drafted,
   *  item_pending, question, draft_error, state_conflict) now carries back
   *  (see onboardingWebSocketService's data interfaces). A no-op if the
   *  entry isn't found (e.g. a stale/duplicate event after the entry was
   *  already resolved), never throws. */
  const markTurnStatus = useCallback(
    (clientEventId: string | undefined, status: ConversationTurn['status'], failureMessage?: string) => {
      if (!clientEventId) return;
      setHistory((prev) =>
        prev.map((turn) => (turn.clientEventId === clientEventId ? { ...turn, status, failureMessage } : turn))
      );
    },
    []
  );
  // Completed captures default to a compact snapshot (see the "coming
  // together" card below) rather than an itemized table; this reveals the
  // full detail view on demand for anyone who wants to double-check exact
  // wording before it's reviewed/published.
  const [showAllCaptured, setShowAllCaptured] = useState(false);

  const [inputSource, setInputSource] = useState<'typed' | 'voice'>('typed');
  const handleVoiceTranscript = useCallback((transcript: string) => {
    setInputValue(transcript);
    setInputSource('voice');
  }, []);
  const {
    state: micState,
    error: micError,
    toggle: handleMicToggle,
    stop: stopVoice,
    reset: resetVoice,
  } = useVoiceTranscript(handleVoiceTranscript);
  const [activeImprovement, setActiveImprovement] = useState<Extract<SmartWizardRequest, { kind: 'improve' }> | null>(null);
  const [improvementProposal, setImprovementProposal] = useState<{ updated: ImproveItemCurrent; changes: string[] } | null>(null);
  const [improvementInstructions, setImprovementInstructions] = useState<string[]>([]);
  const [pendingFocusStage, setPendingFocusStage] = useState<OnboardingStage | null>(null);
  const [focusRetryNonce, setFocusRetryNonce] = useState(0);
  const [applyingImprovement, setApplyingImprovement] = useState(false);
  const handledRequestId = useRef<number | null>(null);
  /** Synchronous guard for websocket batches. React state updates do not
   * flush between back-to-back item_pending/stage_complete/session_complete
   * frames, so a ref is required to make the missing-half contract win in
   * the same event-loop tick as well as on the next render. */
  const requiredPendingStageRef = useRef<OnboardingStage | null>(null);
  const onItemCapturedRef = useRef(onItemCaptured);
  const onItemPendingRef = useRef(onItemPending);

  useEffect(() => {
    onItemCapturedRef.current = onItemCaptured;
    onItemPendingRef.current = onItemPending;
  }, [onItemCaptured, onItemPending]);

  const [capturedItems, setCapturedItems] = useState<CapturedItem[]>([]);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [itemsCaptured, setItemsCaptured] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    onboardingWebSocketService.onConnectionChange((connected) => {
      setConnectionState(connected ? 'connected' : 'disconnected');
      if (connected) {
        setConnectionError(null);
      } else {
        setSessionReady(false);
        // A reconnect creates a new server-side session_started/ready
        // handshake. Do not keep rendering (or answering) the previous
        // connection's question, and do not let autoBegin send `ready`
        // before the replacement session_started arrives.
        setReadySent(false);
        setSessionWelcome(null);
        setCurrentQuestion(null);
        setStageIntroText(null);
      }
    });

    onboardingWebSocketService.onSessionStarted((data) => {
      setConnectionError(null);
      setSessionReady(true);
      const validStages = data.stages.filter(isOnboardingStage);
      if (!isOnboardingStage(data.currentStage) || validStages.length === 0) {
        setConnectionError('The guide returned an unsupported onboarding stage. Please reconnect.');
        return;
      }
      setStages(validStages);
      setCurrentStage(data.currentStage);
      // A returning user already knows why this exists — skip the full
      // orientation and just acknowledge picking up where they left off.
      setIsResumed(data.resumed);
      setSessionWelcome(
        data.resumed
          ? "Welcome back. Let's pick up where you left off."
          : data.sessionWelcome || null
      );
      if (data.stageIntro) setStageIntroText(data.stageIntro);
    });

    onboardingWebSocketService.onQuestion((data) => {
      setConnectionError(null);
      setPendingFocusStage(null);
      setCurrentStage(data.stage);
      setSessionComplete(false);
      setProgressPercent(data.progressPercent);
      setSubmitting(false);
      setDraftError(null);
      setCurrentQuestion(data.question);
      if (data.stageIntro) setStageIntroText(data.stageIntro);
      setInputValue('');
      resetVoice();
      inputRef.current?.focus();
      // A question folded into a turn's own response (e.g. the landmine
      // live-confirmation reply, which produces ONLY a question message,
      // no item event) is itself proof that turn succeeded.
      markTurnStatus(data.clientEventId, 'accepted');
    });

    onboardingWebSocketService.onItemPending((data) => {
      requiredPendingStageRef.current = data.stage;
      setSubmitting(false);
      setDraftError(null);
      setSessionComplete(false);
      setPendingFocusStage(null);
      setCurrentStage(data.stage);
      setProgressPercent(data.progressPercent);
      if (!isValidCapturedDraft(data.stage, data.draft)) {
        console.error('[OnboardingWizard] Dropped malformed item_pending payload', data);
        markTurnStatus(data.clientEventId, 'failed', 'Something went wrong saving that. Try again.');
        return;
      }
      const kind = data.stage as CapturedItem['kind'];
      const draft = data.draft as CapturedItem['draft'];
      const pendingItem: CapturedItem = { id: `pending-${data.stage}`, stage: data.stage, kind, draft };
      onItemPendingRef.current?.(pendingItem);
      setCapturedItems((prev) => {
        const withoutExistingPending = prev.filter(
          (item) => !(item.stage === data.stage && (item.draft as { pending?: boolean }).pending)
        );
        return [...withoutExistingPending, pendingItem];
      });
      setInputValue('');
      if (data.followupQuestion) {
        setCurrentQuestion(data.followupQuestion);
        setStageIntroText(null);
        inputRef.current?.focus();
      }
      resetVoice();
      markTurnStatus(data.clientEventId, 'accepted');
    });

    onboardingWebSocketService.onItemDrafted((data) => {
      if (requiredPendingStageRef.current === data.stage) {
        requiredPendingStageRef.current = null;
      }
      setSubmitting(false);
      setDraftError(null);
      setSessionComplete(false);
      setCurrentStage(data.stage);
      setProgressPercent(data.progressPercent);
      // data.draft is loosely typed (Record<string, any>) on the WS service
      // side since its shape varies by stage — narrowed here to this
      // component's own per-kind interfaces, kept in sync with
      // onboarding_orchestrator_service.py's actual per-stage return shape.
      // Validated before the cast: the backend's capture paths are under
      // active development, so a malformed/version-skewed payload is a real
      // risk, not a theoretical one — better to drop one item with a logged
      // error than crash the whole session on a bad cast.
      if (!isValidCapturedDraft(data.stage, data.draft)) {
        console.error('[OnboardingWizard] Dropped malformed item_drafted payload', data);
        setDraftError('Something you said couldn\'t be saved correctly. Try rephrasing it.');
        markTurnStatus(data.clientEventId, 'failed', 'Something went wrong saving that. Try again.');
        return;
      }
      markTurnStatus(data.clientEventId, 'accepted');
      setItemsCaptured((prev) => prev + 1);
      const kind = data.stage as CapturedItem['kind'];
      const draft = data.draft as CapturedItem['draft'];
      const capturedItem: CapturedItem = { id: nextId(), stage: data.stage, kind, draft };
      // `item_drafted` is emitted only after the draft write succeeds. Clear
      // the just-answered prompt immediately so a completed Q&A never looks
      // like it is still waiting for its answer while the next question event
      // is in flight.
      setCurrentQuestion(null);
      setStageIntroText(null);
      setCaptureConfirmation(buildCaptureConfirmation(capturedItem));
      onItemCapturedRef.current?.(capturedItem);
      setCapturedItems((prev) => {
        // company_context fields are a single value per field in Settings —
        // there's only ever one 'whatYouSell', so a freshly-drafted answer
        // for a field already in the list (e.g. re-answered after a resume)
        // replaces that row instead of appending a duplicate one.
        if (kind === 'company_context') {
          const field = (draft as CompanyContextDraft).field;
          const existingIndex = prev.findIndex((i) => i.kind === 'company_context' && (i.draft as CompanyContextDraft).field === field);
          if (existingIndex !== -1) {
            const next = [...prev];
            const replacement = { id: prev[existingIndex].id, stage: data.stage, kind, draft };
            next[existingIndex] = replacement;
            return next;
          }
        }
        if (kind === 'objections' || kind === 'faqs') {
          const pendingIndex = prev.findIndex((i) => isObjectionOrFaq(i) && i.stage === data.stage && i.draft.pending);
          if (pendingIndex !== -1) {
            const next = [...prev];
            const replacement = { id: prev[pendingIndex].id, stage: data.stage, kind, draft };
            next[pendingIndex] = replacement;
            return next;
          }
        }
        const pendingIndex = prev.findIndex(
          (item) => item.stage === data.stage && (item.draft as { pending?: boolean }).pending
        );
        if (pendingIndex !== -1) {
          const next = [...prev];
          next[pendingIndex] = { id: prev[pendingIndex].id, stage: data.stage, kind, draft };
          return next;
        }
        return [...prev, capturedItem];
      });
      // The server follows this event with the next valid question for the
      // current section. Clear the completed response while that event is in
      // flight so the next item always starts blank.
      setInputValue('');
      resetVoice();
    });

    onboardingWebSocketService.onDraftError((data) => {
      setSubmitting(false);
      setDraftError(data.message);
      // Mark the SPECIFIC history entry that failed, not just the global
      // banner above — a reader scrolled past the top of the thread would
      // otherwise have no way to tell which answer needs retrying.
      markTurnStatus(data.clientEventId, 'failed', data.message);
    });

    onboardingWebSocketService.onStateConflict((data) => {
      setSubmitting(false);
      setDraftError(data.message);
      markTurnStatus(data.clientEventId, 'failed', data.message);
    });

    onboardingWebSocketService.onStageComplete((data) => {
      if (requiredPendingStageRef.current === data.finishedStage) return;
      setCurrentStage(data.nextStage);
      setProgressPercent(data.progressPercent);
      setDraftError(null);
      setCapturedItems((prev) => prev.filter(
        (item) => !(item.stage === data.finishedStage && (item.draft as { pending?: boolean }).pending)
      ));
    });

    onboardingWebSocketService.onSessionComplete((data) => {
      if (requiredPendingStageRef.current) return;
      setSessionComplete(true);
      setItemsCaptured(data.itemsCaptured);
    });

    onboardingWebSocketService.onError((message) => {
      setConnectionState('disconnected');
      setConnectionError(message);
    });

    onboardingWebSocketService.connect(undefined, draftTaskId).catch((err) => {
      setConnectionState('disconnected');
      setConnectionError(err?.message || 'Could not connect');
    });

    return () => {
      stopVoice();
      onboardingWebSocketService.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftTaskId, markTurnStatus]);

  useEffect(() => {
    if (!captureConfirmation) return;
    const timeout = window.setTimeout(() => setCaptureConfirmation(null), 4500);
    return () => window.clearTimeout(timeout);
  }, [captureConfirmation]);

  // Keep the conversation thread scrolled to the newest exchange as it grows,
  // the same way a real chat would — a static scroll position on a growing
  // transcript reads as broken, not as "you're making progress".
  useEffect(() => {
    threadEndRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [history.length, currentQuestion]);

  const handleBegin = useCallback(() => {
    setReadySent(true);
    onboardingWebSocketService.sendReady();
  }, []);

  useEffect(() => {
    if (autoBegin && connectionState === 'connected' && !readySent && sessionWelcome !== null) {
      handleBegin();
    }
  }, [autoBegin, connectionState, handleBegin, readySent, sessionWelcome]);

  useEffect(() => {
    if (!request || handledRequestId.current === request.id) return;
    if (
      request.kind === 'add'
      && requiredPendingStageRef.current
      && requiredPendingStageRef.current !== request.stage
    ) {
      const abandonedStage = requiredPendingStageRef.current;
      requiredPendingStageRef.current = null;
      setCapturedItems((previous) => previous.filter(
        (item) => !(item.stage === abandonedStage && (item.draft as { pending?: boolean }).pending)
      ));
    }
    setSessionComplete(false);
    setImprovementProposal(null);
    setDraftError(null);
    setCurrentStage(request.stage);
    setReadySent(true);
    resetVoice();

    if (request.kind === 'improve') {
      handledRequestId.current = request.id;
      setPendingFocusStage(null);
      setActiveImprovement(request);
      setImprovementInstructions([]);
      setStageIntroText(`Improving ${request.contextLabel}`);
      setCurrentQuestion(`What should I change about this ${request.contextLabel.toLowerCase()}?`);
      setInputValue('');
      inputRef.current?.focus();
      return;
    }

    setActiveImprovement(null);
    setCurrentQuestion(null);
    setStageIntroText(null);
    setPendingFocusStage(request.stage);

    // A raw WebSocket open is not yet a usable conversation: auth token
    // lookup and the start_session exchange happen after onopen. Wait for
    // the server's session_started acknowledgement before focus_topic can
    // become the first frame and be rejected as an invalid session.
    if (connectionState !== 'connected' || !sessionReady) return;
    const focused = request.pendingQuestion
      ? onboardingWebSocketService.focusTopic(request.stage, request.pendingQuestion)
      : onboardingWebSocketService.focusTopic(request.stage);
    if (focused) {
      handledRequestId.current = request.id;
    } else {
      setDraftError('The guide is reconnecting. Your request will be retried when it is ready.');
    }
  }, [connectionState, focusRetryNonce, request, resetVoice, sessionReady]);

  useEffect(() => {
    if (!pendingFocusStage || currentQuestion || connectionError) return;
    const timeout = window.setTimeout(() => {
      setPendingFocusStage(null);
      setDraftError('The smart guide did not open this topic. Check the connection and try again.');
    }, 10000);
    return () => window.clearTimeout(timeout);
  }, [connectionError, currentQuestion, pendingFocusStage]);

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || submitting || micState === 'listening') return;
    setSubmitting(true);
    setDraftError(null);
    // One clientEventId per logical user action, generated ONCE here and
    // reused verbatim by handleRetryTurn below if this exact turn needs
    // retrying — never regenerated on retry, since a fresh id would defeat
    // the server's idempotency check (see onboardingWebSocketService.
    // submitTurn's own doc comment).
    const clientEventId = crypto.randomUUID();
    // Log the exchange into the visible thread immediately — this is an
    // optimistic local echo of what was actually asked and said,
    // independent of how the server ends up structuring it: the
    // conversation the user sees should never depend on extraction
    // succeeding. Its `status` starts 'pending' and is flipped by
    // markTurnStatus once a submit_turn response carrying this same
    // clientEventId actually arrives.
    if (currentQuestion) {
      setHistory((prev) => [
        ...prev,
        { id: nextId(), question: currentQuestion, answer: text, clientEventId, status: 'pending', inputSource },
      ]);
    }
    // Clear the field immediately — the answer is already preserved in the
    // history entry above, and there's no reason to keep it visible in the
    // input while waiting on the network now that it's a single one-shot
    // submit_turn rather than a two-message round trip.
    setInputValue('');
    const submittedInputSource = inputSource;
    setInputSource('typed');
    if (activeImprovement) {
      try {
        const combinedInstruction = improvementInstructions.length > 0
          ? `${improvementInstructions.join('\n')}\nAdditional detail: ${text}`
          : text;
        const response = await improveItem({
          contextLabel: activeImprovement.contextLabel,
          current: activeImprovement.current,
          instruction: combinedInstruction,
        });
        if ('needsInput' in response && response.needsInput) {
          setImprovementInstructions((previous) => [...previous, text]);
          setImprovementProposal(null);
          setCurrentQuestion(response.needsInput);
          markTurnStatus(clientEventId, 'accepted');
          return;
        }
        if (!response.updated || /inferred[—-]verify|needs client clarification/i.test(JSON.stringify(response.updated))) {
          setImprovementInstructions((previous) => [...previous, text]);
          setImprovementProposal(null);
          setCurrentQuestion('What exact information should I use for the missing detail?');
          markTurnStatus(clientEventId, 'accepted');
          return;
        }
        setImprovementProposal({ updated: response.updated, changes: response.changes || [] });
        setImprovementInstructions([]);
        setCurrentQuestion(null);
        markTurnStatus(clientEventId, 'accepted');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Could not generate an update. Try again.';
        setDraftError(message);
        markTurnStatus(clientEventId, 'failed', message);
      } finally {
        setSubmitting(false);
      }
      return;
    }
    onboardingWebSocketService.submitTurn(clientEventId, text, submittedInputSource);
  }, [activeImprovement, currentQuestion, improvementInstructions, inputSource, inputValue, markTurnStatus, micState, submitting]);

  /** Resubmits a specific failed turn using its ORIGINAL clientEventId —
   *  a retried submission of the same logical action, not a new one, so
   *  the server can recognize it (see submitTurn's idempotency contract)
   *  rather than double-processing an answer that may have actually
   *  landed the first time. */
  const handleRetryTurn = useCallback((turn: ConversationTurn) => {
    if (submitting) return;
    if (activeImprovement) {
      setInputValue(turn.answer);
      setHistory((prev) => prev.filter((candidate) => candidate.id !== turn.id));
      setDraftError(null);
      setCurrentQuestion(`What should I change about this ${activeImprovement.contextLabel.toLowerCase()}?`);
      return;
    }
    setSubmitting(true);
    setDraftError(null);
    setHistory((prev) =>
      prev.map((t) => (t.id === turn.id ? { ...t, status: 'pending', failureMessage: undefined } : t))
    );
    onboardingWebSocketService.submitTurn(turn.clientEventId, turn.answer, turn.inputSource);
  }, [activeImprovement, submitting]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFinishEarly = () => {
    // The completion screen replaces this component's JSX in place — it
    // doesn't unmount, so the mount effect's cleanup (which stops capture)
    // never runs here. Without this, a mic left listening when the user
    // clicks "Finish for now" would stay hot through the completion screen.
    if (micState === 'listening') {
      stopVoice();
    }
    onboardingWebSocketService.finish();
  };

  const applyImprovement = async () => {
    if (!activeImprovement || !improvementProposal || applyingImprovement) return;
    setApplyingImprovement(true);
    setDraftError(null);
    try {
      await activeImprovement.onApply(improvementProposal.updated, improvementProposal.changes);
      setCaptureConfirmation(`${activeImprovement.contextLabel} updated in the draft.`);
      setActiveImprovement(null);
      setImprovementInstructions([]);
      setImprovementProposal(null);
      setCurrentQuestion(null);
      setStageIntroText(null);
      setPendingFocusStage(null);
    } catch (error) {
      setDraftError(error instanceof Error ? error.message : 'Could not save this draft update.');
    } finally {
      setApplyingImprovement(false);
    }
  };

  const reviseImprovement = () => {
    if (!activeImprovement) return;
    setImprovementProposal(null);
    setDraftError(null);
    setCurrentQuestion(`What else should I change about this ${activeImprovement.contextLabel.toLowerCase()}?`);
    setInputValue('');
  };

  // ---------------------------------------------------------------------------
  // Row rendering — used for the always-visible "needs your input" pending
  // items and, on demand, the expanded detail view of completed items.
  // ---------------------------------------------------------------------------

  const renderRow = (item: CapturedItem) => {
    if (isCompanyContext(item)) {
      const { field, value } = item.draft;
      const isList = Array.isArray(value);
      return (
        <RowShell
          key={item.id}
          badge={
            <div className="text-xs font-semibold text-ink-muted uppercase tracking-wide">
              {COMPANY_CONTEXT_FIELD_LABELS[field] || field}
            </div>
          }
        >
          <div className="text-sm text-ink-secondary mt-0.5">
            {isList ? (value as string[]).join(', ') || <span className="text-ink-muted italic">Empty</span> : value || <span className="text-ink-muted italic">Empty</span>}
          </div>
        </RowShell>
      );
    }

    if (isObjectionOrFaq(item)) {
      const { question, answer, category, variations } = item.draft;
      const pending = item.draft.pending === true;
      return (
        <RowShell
          key={item.id}
          badge={
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-ink-muted uppercase tracking-wide">
                {item.kind === 'objections' ? 'Objection' : 'FAQ'}
              </span>
              {pending && (
                <span className="text-xs bg-info-tint text-info px-1.5 py-0.5 rounded">
                  Needs answer
                </span>
              )}
              {category && (
                <span className="text-xs bg-surface-subtle text-ink-secondary px-1.5 py-0.5 rounded">
                  {category}
                </span>
              )}
            </div>
          }
        >
          <div className="text-sm font-medium text-ink mt-0.5">{question}</div>
          <div className="text-sm text-ink-secondary mt-1">
            {pending ? <span className="text-signature">Answer this next to save it.</span> : answer}
          </div>
          {variations.length > 0 && (
            <div className="mt-3 pt-3 border-t border-hairline">
              <div className="text-xs text-ink-muted mb-1.5">Also drafted these phrasings. Review them before publishing:</div>
              <div className="space-y-1.5">
                {variations.map((v) => (
                  <div key={v.documentId} className="bg-surface-subtle rounded px-2 py-1.5">
                    <span className="text-sm text-ink-secondary">{v.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </RowShell>
      );
    }

    if (isLandmine(item)) {
      const {
        trigger,
        triggerVariations = [],
        canSay,
        cannotSay,
        landmineType,
        priority,
        quickTips,
        approvalStatus,
        pending,
      } = item.draft;
      return (
        <RowShell
          key={item.id}
          badge={
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-ink-muted uppercase tracking-wide">Landmine</span>
              {pending ? (
                <span className="text-xs bg-info-tint text-info px-1.5 py-0.5 rounded">Needs one detail</span>
              ) : approvalStatus === 'pending' ? (
                <span className="text-xs bg-warning-tint text-warning px-1.5 py-0.5 rounded">Pending approval</span>
              ) : (
                <span className="text-xs bg-success-tint text-success px-1.5 py-0.5 rounded">Approved</span>
              )}
            </div>
          }
        >
          <div className="text-sm font-medium text-ink mt-0.5">{trigger}</div>
          {(landmineType || priority) && (
            <div className="mt-1 text-xs text-ink-muted">
              {landmineType === 'detractor' ? 'Conversation detractor' : 'Legal risk'}
              {priority ? `, ${priority.charAt(0).toUpperCase()}${priority.slice(1)} priority` : ''}
            </div>
          )}
          {triggerVariations.length > 0 && (
            <div className="mt-1 text-xs text-ink-muted">
              {triggerVariations.length} matching phrase{triggerVariations.length === 1 ? '' : 's'} drafted
            </div>
          )}
          {cannotSay.length > 0 && (
            <div className="mt-2 text-sm">
              <span className="text-error font-medium">Don't say: </span>
              <span className="text-ink-secondary">{cannotSay.map((c) => c.point).join('; ')}</span>
            </div>
          )}
          {canSay.length > 0 && (
            <div className="mt-1 text-sm">
              <span className="text-success font-medium">Say instead: </span>
              <span className="text-ink-secondary">{canSay.map((c) => c.point).join('; ')}</span>
            </div>
          )}
          {quickTips && <div className="mt-1 text-xs text-ink-secondary">Tip: {quickTips}</div>}
        </RowShell>
      );
    }

    if (isCompetitor(item)) {
      const { name, advantages, weaknesses, pending } = item.draft;
      return (
        <RowShell
          key={item.id}
          badge={(
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-ink-muted uppercase tracking-wide">Competitor</span>
              {pending && <span className="text-xs bg-info-tint text-info px-1.5 py-0.5 rounded">Needs one detail</span>}
            </div>
          )}
        >
          <div className="text-sm font-medium text-ink mt-0.5">{name}</div>
          {advantages.length > 0 && (
            <div className="mt-2 text-sm">
              <span className="text-success font-medium">Where you win: </span>
              <span className="text-ink-secondary">{advantages.join('; ')}</span>
            </div>
          )}
          {weaknesses.length > 0 && (
            <div className="mt-1 text-sm">
              <span className="text-ink-muted font-medium">Their weak points: </span>
              <span className="text-ink-secondary">{weaknesses.join('; ')}</span>
            </div>
          )}
        </RowShell>
      );
    }

    return null;
  };

  if (sessionComplete && presentation === 'page') {
    return (
      <div className="h-full w-full bg-surface flex items-center justify-center">
        <div className="text-center max-w-sm px-6">
          <h2 className="heading-4 mb-2">Your draft is ready to review</h2>
          <p className="text-ink-secondary mb-1">
            {itemsCaptured === 0 && isResumed
              // itemsCaptured only counts what THIS visit added — a resumed
              // session that lands on already-answered ground everywhere
              // legitimately captures nothing new, but "0 items captured"
              // on its own reads as if the KB itself were empty.
              ? 'Nothing new to add. Everything already captured is still waiting for review.'
              : `${itemsCaptured} item${itemsCaptured === 1 ? '' : 's'} captured. Nothing goes live until you publish the reviewed draft.`}
          </p>
          <p className="text-sm text-ink-muted mb-6">Review the draft next, make edits, then publish the starter set.</p>
          <button
            onClick={onExit}
            className="kb-button kb-button-primary px-5 py-2"
          >
            Go to Knowledge Base
          </button>
        </div>
      </div>
    );
  }

  const orderedStages = stages.length ? stages : [...ONBOARDING_STAGES];
  const stageIndex = currentStage ? orderedStages.indexOf(currentStage) : -1;
  // currentStage is durable transport progress, not necessarily the topic the
  // guide is actively discussing. Once a completed/resumed session is idle,
  // its last stage (normally Competitors) must not masquerade as a current
  // recommendation. Only an active question or explicit user action owns a
  // topic label.
  const displayedStage = pendingFocusStage || activeImprovement?.stage || (currentQuestion ? currentStage : null);
  const chapterLabel = displayedStage
    ? STAGE_DISPLAY_NAMES[displayedStage] || ONBOARDING_STAGE_LABELS[displayedStage]
    : sessionComplete ? null : 'Getting ready';
  // Each stage owns an equal-width segment of the stepper; progressPercent
  // (still driven by the server, unchanged) is mapped onto just the current
  // segment so completed chapters read as fully done and the active one
  // visibly fills in — a concrete "how far into this chapter am I", not an
  // abstract whole-journey percentage.
  const segmentSize = orderedStages.length ? 100 / orderedStages.length : 100;
  const currentSegmentFill = stageIndex >= 0
    ? Math.min(100, Math.max(0, ((progressPercent - stageIndex * segmentSize) / segmentSize) * 100))
    : 0;

  const pendingItems = capturedItems.filter(isPending);
  const completedItems = capturedItems.filter((item) => !isPending(item));
  const latestCompleted = completedItems[completedItems.length - 1];
  const countsByKind = completedItems.reduce<Partial<Record<CapturedItem['kind'], number>>>((acc, item) => {
    acc[item.kind] = (acc[item.kind] || 0) + 1;
    return acc;
  }, {});
  const summaryChips = (Object.keys(countsByKind) as CapturedItem['kind'][]).map((kind) => {
    const count = countsByKind[kind] || 0;
    const [singular, plural] = STAGE_CHIP_NOUNS[kind];
    return { kind, label: `${count} ${count === 1 ? singular : plural}` };
  });

  return (
    <div className="h-full w-full bg-paper flex flex-col">
      {captureConfirmation && (
        <div
          role="status"
          aria-live="polite"
          className="fixed right-5 bottom-5 z-[1500] max-w-sm rounded-lg border border-success bg-surface px-4 py-3 shadow-lg"
        >
          <div className="text-sm font-medium text-success">{captureConfirmation}</div>
          <div className="mt-0.5 text-xs text-ink-muted">It stays private until you publish.</div>
        </div>
      )}
      {/* Header: chapter stepper + exit — a finite, visible journey instead
          of a lone percentage. */}
      <div className="flex-shrink-0 bg-surface border-b border-hairline px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-signature">Smart guide</div>
            {presentation === 'page' && chapterLabel && (
              <div className="mt-0.5 text-sm font-semibold text-ink">{chapterLabel}</div>
            )}
            {presentation === 'page' && stageIndex >= 0 && (
              <div className="text-xs text-ink-muted mt-0.5">
                Step {stageIndex + 1} of {orderedStages.length}
              </div>
            )}
          </div>
          {presentation === 'page' && (
            <button onClick={onExit} className="kb-button kb-button-tertiary px-3 py-2 text-sm">Close</button>
          )}
        </div>
        {presentation === 'page' && <div className="flex gap-1.5">
          {orderedStages.map((stage, idx) => (
            <ProgressBar
              key={stage}
              tone="signature"
              className="flex-1"
              value={idx < stageIndex ? 100 : idx === stageIndex ? currentSegmentFill : 0}
            />
          ))}
        </div>}
        {connectionState === 'disconnected' && !(connectionError && !currentQuestion) && (
          <div className="mt-2 text-xs text-error">
            {connectionError || 'Connection lost. Reconnecting…'}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-2xl mx-auto space-y-6">
          {connectionState === 'connecting' && (
            <div className="text-center text-ink-muted text-sm py-8">Connecting…</div>
          )}

          {connectionError && !currentQuestion && (
            <div className="bg-surface border border-error rounded-xl p-6">
              <div className="text-sm font-semibold text-error">Smart guide is unavailable</div>
              <p className="text-sm text-ink-secondary mt-2 leading-relaxed">{connectionError}</p>
            </div>
          )}

          {connectionState === 'connected' && !connectionError && idleUntilRequested && !request && !readySent && (
            <div role="status" className="rounded-xl border border-hairline bg-surface p-5">
              <p className="text-sm text-ink-secondary">
                Choose Add with guide or Improve with guide when you want help with an item.
              </p>
            </div>
          )}

          {/* Welcome — shown once, before the customer chooses to begin. An
              additive review stays neutral until a guide CTA is invoked. */}
          {connectionState === 'connected' && !connectionError && !idleUntilRequested && !readySent && sessionWelcome && (
            <div className="bg-surface border border-hairline rounded-xl p-6 flex gap-4 items-start">
              <div className="w-9 h-9 rounded-full bg-signature text-on-signature flex items-center justify-center text-sm font-semibold flex-shrink-0">
                AI
              </div>
              <div className="flex-1">
                <p className="text-ink leading-relaxed">{sessionWelcome}</p>
                <button
                  onClick={handleBegin}
                  className="kb-button kb-button-primary mt-4 px-4 py-2 text-sm"
                >
                  {isResumed ? 'Continue' : "Let's begin"}
                </button>
              </div>
            </div>
          )}

          {pendingFocusStage && !currentQuestion && !connectionError && (
            <div role="status" aria-live="polite" className="rounded-xl border border-info bg-surface p-5">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-signature text-on-signature flex items-center justify-center text-xs font-semibold">AI</div>
                <div>
                  <div className="text-sm font-semibold text-ink">
                    Opening {STAGE_DISPLAY_NAMES[pendingFocusStage]}…
                  </div>
                  <p className="mt-0.5 text-xs text-ink-muted">Getting the next useful question ready.</p>
                </div>
              </div>
            </div>
          )}

          {draftError && !currentQuestion && !pendingFocusStage && (
            <div role="alert" className="rounded-xl border border-error bg-error-tint p-5 text-sm text-ink">
              <p>{draftError}</p>
              {request && (
                <button
                  type="button"
                  className="mt-3 min-h-[44px] rounded-lg border border-error bg-surface px-3 font-semibold text-error hover:bg-error-tint focus:outline-none focus:ring-2 focus:ring-signature"
                  onClick={() => {
                    handledRequestId.current = null;
                    setDraftError(null);
                    setPendingFocusStage(request.stage);
                    // A focus retry cannot succeed until start_session has
                    // been acknowledged. Rebuild a transport that is open
                    // but still stuck before session_started instead of
                    // repeatedly attempting a no-op focus request.
                    if (!sessionReady) {
                      setConnectionState('connecting');
                      onboardingWebSocketService.connect(undefined, draftTaskId).catch((err) => {
                        setConnectionState('disconnected');
                        setConnectionError(err?.message || 'Could not connect');
                      });
                    }
                    setFocusRetryNonce((value) => value + 1);
                  }}
                >
                  Try opening again
                </button>
              )}
            </div>
          )}

          {/* The conversation so far — kept visible as a real thread instead
              of clearing the moment each answer is filed. */}
          {history.length > 0 && (
            <div className="space-y-4">
              {history.map((turn) => (
                <div key={turn.id} className="space-y-2">
                  <div className="flex gap-3 items-start">
                    <div className="w-7 h-7 rounded-full bg-signature text-on-signature flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      AI
                    </div>
                    <div className="bg-surface border border-hairline rounded-xl rounded-tl-sm px-3.5 py-2 text-sm text-ink-secondary max-w-lg">
                      {turn.question}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div
                      className={`rounded-xl rounded-tr-sm px-3.5 py-2 text-sm max-w-lg text-white ${
                        turn.status === 'failed' ? 'bg-error' : 'bg-signature'
                      } ${turn.status === 'pending' ? 'opacity-60' : ''}`}
                    >
                      {turn.answer}
                    </div>
                    {turn.status === 'pending' && (
                      <span className="text-xs text-ink-muted" data-testid="turn-status-pending">
                        Sending…
                      </span>
                    )}
                    {turn.status === 'failed' && (
                      <div className="flex items-center gap-2" data-testid="turn-status-failed">
                        <span className="text-xs text-error">
                          {turn.failureMessage || 'Failed to send.'}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRetryTurn(turn)}
                          disabled={submitting}
                          className="text-xs font-medium text-signature hover:text-signature-strong underline disabled:opacity-50"
                        >
                          Try again
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeImprovement && improvementProposal && (() => {
            const preview = improveValueToText(improvementProposal.updated);
            return (
              <div className="rounded-xl border border-info bg-surface p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-info">Suggested update</div>
                {preview.question && <p className="mt-2 text-sm font-semibold text-ink">{preview.question}</p>}
                <p className="mt-1 whitespace-pre-wrap text-sm text-ink-secondary">{preview.answer}</p>
                {improvementProposal.changes.length > 0 && (
                  <p className="mt-2 text-xs text-ink-muted">{improvementProposal.changes.join(', ')}</p>
                )}
                {draftError && <p className="mt-2 text-sm text-error">{draftError}</p>}
                <div className="mt-4 flex flex-wrap justify-end gap-2">
                  <button type="button" onClick={reviseImprovement} className="kb-button kb-button-secondary px-3 py-2 text-sm">Ask for another change</button>
                  <button type="button" disabled={applyingImprovement} onClick={applyImprovement} className="kb-button kb-button-primary px-3 py-2 text-sm">
                    {applyingImprovement ? 'Saving…' : 'Apply to draft'}
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Live question + editable input card */}
          {readySent && currentQuestion && (
            <div className="bg-surface border border-hairline rounded-xl p-6">
              <div className="flex gap-4 items-start">
                <div className="w-9 h-9 rounded-full bg-signature text-on-signature flex items-center justify-center text-sm font-semibold flex-shrink-0">
                  AI
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-lg font-semibold text-ink">{currentQuestion}</div>
                </div>
              </div>

              <div className="mt-4 relative">
                <textarea
                  aria-label="Answer to Smart Guide"
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    setInputSource('typed');
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Type, or tap the mic to speak your answer…"
                  rows={3}
                  disabled={connectionState !== 'connected' || submitting || micState === 'listening'}
                  className="w-full resize-none border border-ink-border rounded-lg pl-3 pr-11 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-signature disabled:bg-surface-subtle"
                />
                <button
                  onClick={handleMicToggle}
                  disabled={connectionState !== 'connected' || submitting}
                  title={micState === 'listening' ? 'Stop listening' : 'Speak your answer'}
                  aria-label={micState === 'listening' ? 'Stop listening' : 'Speak your answer'}
                  className={`absolute top-2.5 right-2.5 p-1.5 rounded-md disabled:opacity-40 disabled:cursor-not-allowed ${
                    micState === 'listening'
                      ? 'bg-error-tint text-error'
                      : 'text-ink-muted hover:bg-surface-subtle hover:text-ink-secondary'
                  }`}
                >
                  {micState === 'listening' ? (
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <rect x="5" y="5" width="10" height="10" rx="1.5" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path d="M10 12a2.5 2.5 0 0 0 2.5-2.5v-4a2.5 2.5 0 0 0-5 0v4A2.5 2.5 0 0 0 10 12Z" />
                      <path d="M5.5 9a.75.75 0 0 0-1.5 0 6 6 0 0 0 5.25 5.955V17H7.75a.75.75 0 0 0 0 1.5h4.5a.75.75 0 0 0 0-1.5H10.75v-2.045A6 6 0 0 0 16 9a.75.75 0 0 0-1.5 0 4.5 4.5 0 0 1-9 0Z" />
                    </svg>
                  )}
                </button>
              </div>

              {micState === 'listening' && (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs text-ink-muted">
                  <span className="inline-block w-1.5 h-1.5 bg-error rounded-full animate-pulse" />
                  Listening. Tap the mic again when you're done, then edit anything before continuing.
                </div>
              )}
              {micError && micState === 'idle' && (
                <div className="mt-1.5 text-xs text-error">{micError}</div>
              )}

              {draftError && (
                <div className="mt-3 bg-warning-tint border border-warning text-ink text-sm rounded-lg px-3 py-2">
                  {draftError}
                </div>
              )}

              <div className="flex items-center justify-between mt-4">
                <div className="flex gap-3">
                  {presentation === 'page' && (
                    <button onClick={handleFinishEarly} className="text-xs text-ink-muted hover:text-ink-secondary">Finish for now</button>
                  )}
                </div>
                <button
                  onClick={handleSend}
                  disabled={!inputValue.trim() || connectionState !== 'connected' || submitting || micState === 'listening'}
                  className="kb-button kb-button-primary px-4 py-2 text-sm"
                >
                  {submitting ? 'Saving…' : 'Continue'}
                </button>
              </div>
            </div>
          )}

          {/* Items still needing one more detail stay fully visible — this
              is active, in-progress conversation, not filed history yet. */}
          {showCapturedList && pendingItems.length > 0 && (
            <div className="space-y-2.5">{pendingItems.map(renderRow)}</div>
          )}

          {/* Everything filed so far — a compact, growing snapshot instead
              of an itemized table, with full detail available on demand. */}
          {showCapturedList && completedItems.length > 0 && (
            <div className="bg-signature-tint border border-hairline rounded-xl p-4">
              <div className="text-sm font-semibold text-ink">
                Your AI sales assistant is coming together
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {summaryChips.map((chip) => (
                  <span
                    key={chip.kind}
                    className="text-xs bg-surface border border-hairline rounded-full px-2.5 py-1 text-ink-secondary"
                  >
                    {chip.label}
                  </span>
                ))}
              </div>
              {latestCompleted && (
                <div className="mt-3 text-sm text-ink-secondary">
                  Just added: <span className="font-medium text-ink">{summaryHighlightText(latestCompleted)}</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowAllCaptured((v) => !v)}
                className="mt-3 text-xs text-signature hover:underline"
              >
                {showAllCaptured ? 'Hide details' : 'See everything captured'}
              </button>
              {showAllCaptured && (
                <div className="mt-4 space-y-5">
                  {orderedStages.map((stage) => {
                    const items = completedItems.filter((i) => i.stage === stage);
                    if (!items.length) return null;
                    return (
                      <div key={stage}>
                        <div className="text-xs font-medium text-ink-muted mb-2">{ONBOARDING_STAGE_LABELS[stage]}</div>
                        <div className="space-y-2.5">{items.map(renderRow)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div ref={threadEndRef} />
        </div>
      </div>

    </div>
  );
};

export default OnboardingWizard;
